'use client';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import type { Theme } from '@kausal/themes/types';
import { useTranslations } from 'next-intl';

import { transientOptions } from '@common/themes/styles/styled';

import { useContainedLayout } from '@/common/hooks/use-contained-layout';
import { type HeroImageRenditions, getImageSrcSet } from '@/common/images';
import ContainedHeaderImage from '@/components/common/ContainedHeaderImage';
import RichText from '@/components/common/RichText';
import { Container } from '@/components/common/layout/LayoutGrid';

import { ImageCredit } from '../common/ImageCredit';
import { HeroCard } from './heroStyles';

const getHeight = (theme: Theme, defaultHeight: string) =>
  theme.settings.frontHero?.height ?? defaultHeight;

function getHeroMinHeight(theme: Theme, defaultHeight: string, focalBoxAspectRatio?: number) {
  const height = getHeight(theme, defaultHeight);

  // If the user specified a focal box in Wagtail, ensure container is tall enough to show it all
  if (focalBoxAspectRatio) {
    return `max(${height}, calc(100vw / ${focalBoxAspectRatio}))`;
  }

  return height;
}

const Hero = styled.div<{ $focalBoxAspectRatio?: number }>`
  width: 100%;
  position: relative;
  background-color: ${(props) => props.theme.brandDark};
  padding: 0 0 2rem;

  ${(props) => props.theme.breakpoints.up('md')} {
    display: flex;
    min-height: ${({ theme, $focalBoxAspectRatio }) =>
      getHeroMinHeight(theme, '24rem', $focalBoxAspectRatio)};
    padding: 0;
  }

  ${(props) => props.theme.breakpoints.up('lg')} {
    min-height: ${({ theme, $focalBoxAspectRatio }) =>
      getHeroMinHeight(theme, '28rem', $focalBoxAspectRatio)};
  }

  ${(props) => props.theme.breakpoints.up('xl')} {
    min-height: ${({ theme, $focalBoxAspectRatio }) =>
      getHeroMinHeight(theme, '30rem', $focalBoxAspectRatio)};
  }
`;

const HeroImage = styled.div`
  min-height: 14rem;
  position: relative;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up('md')} {
    position: absolute;
    width: 100%;
    height: 100%;
  }
`;

const HeroImageImg = styled.img<{ $imageAlign: string }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${(props) => props.$imageAlign};
`;

const HeroContent = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
`;

const MainCard = styled(HeroCard, transientOptions)<{ $alignment: string }>`
  max-width: 576px;
  margin: -2rem auto 0;

  ${(props) => props.theme.breakpoints.up('md')} {
    margin: var(--block-padding-top)
      ${(props) => {
        switch (props.$alignment) {
          case 'left':
            return '0 var(--block-padding-bottom) 0';
          case 'right':
            return '0 var(--block-padding-bottom) auto';
          case 'center':
            return 'auto';
          default:
            return '0';
        }
      }};
  }
`;

/* In the contained layout the hero text sits in the page flow below the image,
 * aligned with the page content, instead of in a card over the image */
const ContainedHeroContent = styled.div`
  padding-top: ${({ theme }) => theme.spaces.s200};

  ${(props) => props.theme.breakpoints.up('md')} {
    padding-top: ${({ theme }) => theme.spaces.s300};
  }

  h1 {
    font-size: ${(props) => props.theme.fontSizeLg};
    margin-bottom: ${(props) => props.theme.spaces.s100};

    &:last-child {
      margin-bottom: 0;
    }

    ${(props) => props.theme.breakpoints.up('md')} {
      font-size: ${(props) => props.theme.fontSizeXl};
    }
  }

  .lead-content {
    max-width: 992px;
    font-size: ${(props) => props.theme.fontSizeBase};
    line-height: ${(props) => props.theme.lineHeightMd};
    font-family: ${(props) =>
      `${props.theme.fontFamilyContent}, ${props.theme.fontFamilyFallback}`};

    ${(props) => props.theme.breakpoints.up('md')} {
      font-size: ${(props) => props.theme.fontSizeMd};
    }
  }
`;

interface FocalBoxInfo {
  focalPointX: number;
  focalPointY: number;
  focalPointWidth: number;
  focalPointHeight: number;
  imageWidth: number;
  imageHeight: number;
}

interface HeroFullImageProps {
  id?: string;
  image: HeroImageRenditions;
  imageAlign?: string;
  focalBox?: FocalBoxInfo;
  title?: string;
  lead?: string;
  altText?: string;
  imageCredit?: string;
}

const HeroFullImage = (props: HeroFullImageProps) => {
  const {
    id = '',
    image,
    imageAlign = 'center center',
    focalBox,
    title,
    lead,
    altText,
    imageCredit,
  } = props;

  const t = useTranslations();
  const theme = useTheme();
  const containedLayout = useContainedLayout();

  const contentAlignment = theme.settings?.frontHero?.cardPlacement ?? 'left';
  const contentColor = theme.settings?.frontHero?.color ?? 'light';

  const showContentBox = title || lead;

  // aspectRatio = imageWidth / focalBoxHeight ensures the container is tall enough
  // when the image is scaled to cover the container width
  const focalBoxAspectRatio = focalBox
    ? focalBox.imageWidth / focalBox.focalPointHeight
    : undefined;

  if (containedLayout) {
    return (
      <div id={id}>
        <ContainedHeaderImage
          image={image}
          imageAlign={imageAlign}
          altText={altText}
          imageCredit={imageCredit}
        />
        {showContentBox && (
          <Container>
            <ContainedHeroContent>
              {title && <h1>{title}</h1>}
              {lead ? <RichText html={lead} className="lead-content" /> : null}
            </ContainedHeroContent>
          </Container>
        )}
      </div>
    );
  }

  const fallbackSrc = (image.fullMedium ?? image.full ?? image.fullSmall)?.src;

  return (
    <Hero id={id} $focalBoxAspectRatio={focalBoxAspectRatio}>
      {fallbackSrc && (
        <HeroImage>
          <HeroImageImg
            src={fallbackSrc}
            srcSet={getImageSrcSet([image.fullSmall, image.fullMedium, image.full])}
            sizes="100vw"
            alt={altText ?? ''}
            $imageAlign={imageAlign}
          />
        </HeroImage>
      )}
      {imageCredit && (
        <ImageCredit
          aria-hidden={altText ? undefined : true}
        >{`${t('image-credit')}: ${imageCredit}`}</ImageCredit>
      )}
      {showContentBox && (
        <Container>
          <HeroContent>
            <MainCard $alignment={contentAlignment} $cardColor={contentColor}>
              <h1>{title}</h1>
              {lead ? <RichText html={lead} className="lead-content" /> : null}
            </MainCard>
          </HeroContent>
        </Container>
      )}
    </Hero>
  );
};

export default HeroFullImage;
